import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Upload, CheckCircle2, XCircle, Clock, AlertTriangle, FileText,
  Calendar, Shield, Trash2, Download, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  missing:       { label: 'Missing',       color: 'bg-slate-50 text-slate-400 border-slate-200',    icon: FileText,      dot: 'bg-slate-300' },
  uploaded:      { label: 'Pending Review', color: 'bg-amber-50 text-amber-600 border-amber-200',   icon: Clock,         dot: 'bg-amber-400' },
  pending_review:{ label: 'Pending Review', color: 'bg-amber-50 text-amber-600 border-amber-200',   icon: Clock,         dot: 'bg-amber-400' },
  approved:      { label: 'Approved',       color: 'bg-emerald-50 text-emerald-600 border-emerald-200', icon: CheckCircle2, dot: 'bg-emerald-500' },
  rejected:      { label: 'Rejected',       color: 'bg-red-50 text-red-500 border-red-200',         icon: XCircle,       dot: 'bg-red-400' },
  expired:       { label: 'Expired',        color: 'bg-red-50 text-red-500 border-red-200',         icon: AlertTriangle, dot: 'bg-red-400' },
  expiring_soon: { label: 'Expiring Soon',  color: 'bg-amber-50 text-amber-600 border-amber-200',   icon: AlertTriangle, dot: 'bg-amber-400' },
};

export default function DocumentWorkflow({ onAllUploaded }) {
  const [requirements, setRequirements] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [expDate, setExpDate] = useState('');

  const fetchRequirements = useCallback(async () => {
    try {
      const [reqRes, reqsRes] = await Promise.all([
        axios.get(`${API}/documents/requirements`, { withCredentials: true }),
        axios.get(`${API}/documents/requests`, { withCredentials: true }),
      ]);
      setRequirements(reqRes.data);
      setRequests(reqsRes.data);
      const allDone = reqRes.data.length > 0 && reqRes.data.filter(r => r.status !== 'missing').length === reqRes.data.length;
      if (allDone && onAllUploaded) onAllUploaded();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [onAllUploaded]);

  useEffect(() => { fetchRequirements(); }, [fetchRequirements]);

  const triggerUpload = (docType) => {
    setUploadTarget(docType);
    setExpDate('');
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    setUploading(uploadTarget);
    try {
      const formData = new FormData();
      formData.append('file', file);
      let url = `${API}/documents/upload-typed?category=${uploadTarget}`;
      if (expDate) url += `&expiration_date=${expDate}`;
      await axios.post(url, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded!');
      fetchRequirements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
      setUploadTarget(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const completedCount = requirements.filter(r => r.status !== 'missing').length;
  const totalCount = requirements.length;
  const progressPct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-slate-400" /></div>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="document-workflow">
      <h1 className="text-3xl font-['Cabinet_Grotesk'] font-bold mb-1 text-slate-900">Documents</h1>
      <p className="text-sm text-slate-500 mb-6">Upload and manage your required brokerage documents.</p>

      {/* Hidden file input */}
      <input type="file" ref={fileInputRef} onChange={handleFileSelected} className="hidden" data-testid="doc-workflow-file-input" />

      {/* Progress */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="doc-progress">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-slate-700">{completedCount} of {totalCount} documents submitted</span>
          <span className="text-xs font-semibold text-[#002FA7]">{Math.round(progressPct)}%</span>
        </div>
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#002FA7] rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
        {completedCount === totalCount && totalCount > 0 && (
          <div className="flex items-center gap-2 text-emerald-600 text-xs mt-3">
            <CheckCircle2 size={14} /> All required documents submitted!
          </div>
        )}
      </div>

      {/* Pending Requests from Admin */}
      {requests.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="doc-requests">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">Admin Requests</span>
          </div>
          {requests.map(req => (
            <div key={req.id} className="text-xs text-amber-700 mb-1">
              <strong>{req.document_type.replace(/_/g, ' ')}</strong>
              {req.notes && <span className="text-amber-600 ml-1">— {req.notes}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Requirements Grid */}
      <div className="space-y-3">
        {requirements.map(req => {
          const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.missing;
          const isUploading = uploading === req.type;
          return (
            <div key={req.type} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center justify-between gap-4" data-testid={`doc-req-${req.type}`}>
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${cfg.dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-slate-800">{req.label}</span>
                    <Badge variant="outline" className={`text-[9px] ${cfg.color}`}>
                      <cfg.icon size={10} className="mr-1" />{cfg.label}
                    </Badge>
                    {req.requested && <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-600 border-blue-200">Requested</Badge>}
                  </div>
                  {req.expiration_date && (
                    <div className="flex items-center gap-1 text-[10px] text-slate-400">
                      <Calendar size={10} />
                      Expires: {new Date(req.expiration_date).toLocaleDateString()}
                      {req.status === 'expiring_soon' && <span className="text-amber-600 font-semibold ml-1">(expires within 30 days)</span>}
                      {req.status === 'expired' && <span className="text-red-500 font-semibold ml-1">(expired)</span>}
                    </div>
                  )}
                  {req.request_notes && (
                    <div className="text-[10px] text-slate-400 mt-0.5">Note: {req.request_notes}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {req.can_expire && req.status === 'missing' && (
                  <Input
                    type="date"
                    value={uploadTarget === req.type ? expDate : ''}
                    onChange={(e) => { setUploadTarget(req.type); setExpDate(e.target.value); }}
                    className="w-32 h-8 text-xs border-slate-200 rounded"
                    placeholder="Exp date"
                    data-testid={`exp-date-${req.type}`}
                  />
                )}
                {(req.status === 'missing' || req.status === 'rejected' || req.status === 'expired') ? (
                  <Button
                    onClick={() => triggerUpload(req.type)}
                    disabled={isUploading}
                    size="sm"
                    className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg text-xs h-8"
                    data-testid={`upload-${req.type}`}
                  >
                    {isUploading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                    {req.status === 'rejected' ? 'Re-upload' : req.status === 'expired' ? 'Renew' : 'Upload'}
                  </Button>
                ) : (
                  <div className="flex items-center gap-1">
                    {req.doc_id && (
                      <a
                        href={`${API}/documents/${req.doc_id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-slate-300 hover:text-[#002FA7] transition-colors"
                        data-testid={`download-${req.type}`}
                      >
                        <Download size={14} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
